import LoginRegister from "@/assets/img/loginRegister.png";
import LogoLight from "@/assets/img/logo-light.png";
import Spinner from "@/components/loader/Spinner";
import { useUser } from "@/contexts/UserContext";
import axiosInstanceNoAuth from "@/utils/axiosConfigNoAuth";
import { initializeServiceWorker } from "@/utils/pushNotification";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { login, user } = useUser();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    initializeServiceWorker("/");
  }, []);

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_APP_REDBIRDPOSBE_LOGIN ?? "";
      const response = await axiosInstanceNoAuth.post(apiUrl, {
        username,
        password,
      });

      if (response.data) {
        const userData = {
          id: response.data.id,
          username: response.data.username,
          email: response.data.email,
          first_name: response.data.first_name,
          last_name: response.data.last_name,
          groups: response.data.groups,
          access_token: response.data.access_token,
          avatar: response.data.avatar,
          branches: response.data.branches,
          owner: response.data.owner,
        };

        console.log("User data", userData);
        login(userData);
        localStorage.setItem("access_token", response.data.access_token);
        localStorage.setItem("user_data", JSON.stringify(userData));
        toast.success("Login successful", {
          description: "Welcome back!",
          duration: 3000,
        });
        navigate("/");
      }
      setIsLoading(false);
    } catch (error) {
      toast.error("Invalid credentials", {
        description: "Please check your username and password",
        duration: 4000,
      });
      console.error("Error logging in:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="flex min-h-screen overflow-hidden">
      <Link to="/" className="absolute left-0 top-0 p-4">
        <img src={LogoLight} alt="Home" className="w-30 h-12" />
      </Link>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <Spinner />
        </div>
      )}
      <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-20">
          <div>
            <h2 className="mb-8 mt-6 text-center text-3xl font-bold text-gray-900 md:text-5xl">
              {t("account.login.welcome")}
            </h2>
            <div className="text-center text-2xl font-bold md:text-3xl">
              <span className="text-black">
                {t("account.login.login_to")}&nbsp;
              </span>
              <span className="text-red-600">
                {t("account.login.redtitle")}
              </span>
              <span className="text-black">
                {t("account.login.service_name")}
              </span>
            </div>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <input type="hidden" name="remember" value="true" />
            <div>
              <label
                htmlFor="username"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                {t("account.login.username_label")}{" "}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                className="relative block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                placeholder={t("account.login.username_placeholder")}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="relative mt-4">
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                {t("account.login.password_label")}{" "}
              </label>
              <div className="flex items-center rounded-md border border-gray-300">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  className="block w-full appearance-none rounded-l-md px-3 py-2 text-gray-900 focus:border-red-500 focus:outline-none focus:ring-red-500 sm:text-sm"
                  placeholder={t("account.login.password_placeholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="rounded-r-md border-l border-gray-300 px-3 py-2 focus:outline-none"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember_me"
                  name="remember_me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                />
                <label
                  htmlFor="remember_me"
                  className="ml-2 block text-sm text-gray-900"
                >
                  {t("account.login.remember_me")}
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/reset-password"
                  className="font-medium text-red-600 hover:text-red-500"
                >
                  {t("account.login.forgot_password")}
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="group relative flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {t("account.login.login_button")}
              </button>
            </div>
          </form>
          <div className="text-center text-sm font-medium">
            {t("account.login.no_account")}{" "}
            <Link to="/register" className="ml-2 font-medium text-red-600">
              {t("account.login.register")}
            </Link>
          </div>
        </div>
      </div>

      <div className="relative hidden w-1/2 lg:block">
        <div className="absolute bottom-0 right-0 top-0 w-3/5 bg-red-600"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={LoginRegister}
            className="object-contain"
            style={{ maxHeight: "80%" }}
            alt="Login Visual"
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
